import React, { useRef, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Loader2, X, FileText, Link2 } from "lucide-react";
import { toast } from "sonner";

/**
 * CloudinaryUpload - reusable upload widget.
 * Props:
 *  - value: current URL stored in parent
 *  - onChange: (url) => void
 *  - folder: target Cloudinary folder (must be in backend allow-list).
 *      e.g. "brands/", "products/", "reels/", "kyc/", "sliders/"
 *  - resourceType: "image" | "video" | "raw" (PDFs use "raw")
 *  - accept: HTML file accept string. Defaults derived from resourceType.
 *  - label: optional field label shown above input
 *  - allowUrlPaste: also show a URL input (default true)
 *  - previewClassName: classes applied to <img> preview
 */
const CloudinaryUpload = ({
  value,
  onChange,
  folder = "uploads/",
  resourceType = "image",
  accept,
  label,
  allowUrlPaste = true,
  previewClassName = "h-24 w-24 object-cover rounded-lg border",
  testId,
}) => {
  const fileInputRef = useRef(null);
  const { getAuthHeaders } = useAuth();
  const [uploading, setUploading] = useState(false);

  const defaultAccept =
    resourceType === "video"
      ? "video/*"
      : resourceType === "raw"
      ? "application/pdf,.pdf"
      : "image/*";

  const handleSelectFile = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    e.target.value = ""; // allow re-uploading the same file

    setUploading(true);
    try {
      // 1. Get signature from our backend
      const sigRes = await axios.get(`${API}/cloudinary/signature`, {
        params: { resource_type: resourceType, folder },
        headers: getAuthHeaders(),
      });
      const sig = sigRes.data;

      // 2. Upload directly to Cloudinary
      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", sig.timestamp);
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);

      const endpointType = resourceType === "raw" ? "raw" : resourceType;
      const uploadUrl = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${endpointType}/upload`;

      const upRes = await fetch(uploadUrl, { method: "POST", body: form });
      const data = await upRes.json();

      if (!upRes.ok || !data.secure_url) {
        throw new Error(data.error?.message || "Cloudinary upload failed");
      }

      onChange(data.secure_url);
      toast.success("Uploaded successfully");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const isImage = value && (resourceType === "image" || /\.(jpg|jpeg|png|webp|gif|svg)(\?|$)/i.test(value));
  const isPdf = value && /\.pdf(\?|$)/i.test(value);

  return (
    <div className="space-y-2" data-testid={testId}>
      {label && <label className="text-sm font-medium text-gray-700">{label}</label>}

      <div className="flex items-start gap-3 flex-wrap">
        {/* Preview */}
        {value ? (
          isImage ? (
            <img
              src={value}
              alt="preview"
              className={previewClassName}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div className="h-24 w-24 rounded-lg border flex flex-col items-center justify-center bg-gray-50 text-gray-500 text-xs px-2 text-center">
              {isPdf ? <FileText className="w-6 h-6 mb-1" /> : <Link2 className="w-6 h-6 mb-1" />}
              <span className="truncate w-full">{isPdf ? "PDF" : "Media"}</span>
            </div>
          )
        ) : (
          <div className="h-24 w-24 rounded-lg border border-dashed flex items-center justify-center bg-gray-50 text-gray-400 text-xs">
            No file
          </div>
        )}

        {/* Controls */}
        <div className="flex-1 min-w-[180px] space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={accept || defaultAccept}
            className="hidden"
            onChange={handleFileChange}
            data-testid={testId ? `${testId}-input` : undefined}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSelectFile}
              disabled={uploading}
              data-testid={testId ? `${testId}-btn` : undefined}
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  {value ? "Change" : "Upload"}
                </>
              )}
            </Button>
            {value && !uploading && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange("")}
                className="text-red-500 hover:text-red-700"
              >
                <X className="w-4 h-4 mr-1" />
                Remove
              </Button>
            )}
          </div>

          {allowUrlPaste && (
            <Input
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder="or paste URL..."
              className="text-xs"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CloudinaryUpload;
